import { Grid, Theme, Typography, useTheme } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import { useEffect, useState } from 'react';
import MessageUsersFormEditiorPreview from '../services/messageUsers/sendMessage/MessageUsersFormEditiorPreview';
import OnlyFansQuillMessageEditor from './OnlyFansQuillMessageEditor';
import StyledCardTitleBar from './StyledCardTitleBar';
import StyledDialogActions from './StyledDialogActions';

export type OnlyFansMessageInputDialogProps = {
    open: boolean;
    onClose: () => void;
    onSubmit: (value: string) => void;
    title?: string;
    contentText?: string | null;
    initialValue?: string;
    cancelButtonText?: string;
    submitButtonText?: string;
    allowEmpty?: boolean;
    /** Show the %USERNAME% button in the Quill toolbar. Default false. */
    showUsernameButton?: boolean;
    /** Show the "Load from Captions" template button. Default false. */
    showSaveTemplate?: boolean;
    /** Show OnlyFans-style message preview */
    showPreview?: boolean;
    fullScreen?: boolean;
};

/**
 * Dialog that uses OnlyFansQuillMessageEditor for multiline/rich message input.
 * Value is OF-class HTML; same format as OnlyFansQuillMessageEditor (valueOF / onChangeOF).
 */
const OnlyFansMessageInputDialog = ({
    open,
    onClose,
    onSubmit,
    title = 'Enter message',
    contentText,
    initialValue = '',
    cancelButtonText = 'Cancel',
    submitButtonText = 'Save',
    allowEmpty = false,
    showUsernameButton = false,
    showSaveTemplate = false,
    showPreview = false,
    fullScreen = false,
}: OnlyFansMessageInputDialogProps) => {
    const theme: Theme = useTheme();
    const [value, setValue] = useState(initialValue);

    useEffect(() => {
        if (open) {
            setValue(initialValue);
        }
    }, [open, initialValue]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(value);
        onClose();
    };

    const handleClose = () => {
        onClose();
    };

    const trimmed = value.trim();
    const canSubmit = allowEmpty || trimmed.length > 0;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
            <StyledCardTitleBar title={title} />
            <form onSubmit={handleSubmit}>
                <DialogContent sx={{ p: 1.5 }}>
                    {contentText != null && contentText !== '' ? (
                        <DialogContentText gutterBottom sx={{ mb: 1 }}>
                            {contentText}
                        </DialogContentText>
                    ) : null}
                    <OnlyFansQuillMessageEditor
                        valueOF={value}
                        onChangeOF={setValue}
                        placeholder="Enter message..."
                        showUsernameButton={showUsernameButton}
                        showSaveTemplate={showSaveTemplate}
                    />
                    {showPreview && (
                        <Grid container spacing={1} sx={{ mt: 1 }}>
                            <Grid item xs={12}>
                                <Typography variant="body2" color={'text.secondary'}>
                                    Preview
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <MessageUsersFormEditiorPreview
                                    message={value}
                                    taggedCreators={[]}
                                    taggedReleaseForms={[]}
                                    lockMessage={false}
                                    lockMessagePrice={0}
                                    lockedText={false}
                                    theme={theme}
                                    disabled={false}
                                />
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <StyledDialogActions>
                    <Button onClick={handleClose}>{cancelButtonText}</Button>
                    <Button type="submit" variant="contained" color="primary" disabled={!canSubmit}>
                        {submitButtonText}
                    </Button>
                </StyledDialogActions>
            </form>
        </Dialog>
    );
};

export default OnlyFansMessageInputDialog;
